/**
 * Palette model for the Appearance editor.
 *
 * The editor works in five roles (background, text, main, second, accent). Each surface it themes
 * stores them under its own keys, and the conversions live here:
 *   link-in-bio  style.palette        base-100, neutral, primary, secondary, accent
 *   Easel site   page_content.theme   background, ink, primary, primaryLight, accent   (planned)
 */
import type { ColorPalette, PaletteRoles } from '../../types'

export type RoleKey = keyof PaletteRoles

/** The five roles, in the order and words an artist sees them. */
export const ROLE_FIELDS: { key: RoleKey; label: string; help: string }[] = [
  { key: 'background', label: 'Background', help: 'The page behind everything' },
  { key: 'text', label: 'Text', help: 'Your name, bio and headings' },
  { key: 'main', label: 'Main colour', help: 'Buttons and most cards' },
  { key: 'second', label: 'Second colour', help: 'Alternate cards' },
  { key: 'accent', label: 'Accent', help: 'Highlights and details' },
]

export interface NamedPalette {
  name: string
  description?: string
  colors: PaletteRoles
}

export const PRESETS: NamedPalette[] = [
  { name: 'Gallery White', description: 'Quiet and neutral; lets the work lead.', colors: { background: '#FFFFFF', text: '#111827', main: '#1F2937', second: '#6B7280', accent: '#C08457' } },
  { name: 'Plum & Cream', description: 'Warm cream with deep plum cards.', colors: { background: '#FDFBF7', text: '#111827', main: '#68315A', second: '#562B4B', accent: '#DFB5D8' } },
  { name: 'Coastal', description: 'Sea blues with a sandy accent.', colors: { background: '#F4F8FA', text: '#0F1E2A', main: '#1E5B7A', second: '#3E8EB0', accent: '#F2B880' } },
  { name: 'Sage', description: 'Soft greens and a mustard pop.', colors: { background: '#F6F4EE', text: '#1E2A1C', main: '#5B7553', second: '#8DA47E', accent: '#E3B23C' } },
  { name: 'Terracotta', description: 'Earthy clay with a teal counterpoint.', colors: { background: '#FBF5EF', text: '#2B1A14', main: '#B5543C', second: '#D98E5F', accent: '#2F6F6A' } },
  { name: 'Blush', description: 'Rosy and light, with a cool accent.', colors: { background: '#FFF7F8', text: '#3A1F2B', main: '#C2577A', second: '#E89AAE', accent: '#6C9BD2' } },
  { name: 'Midnight', description: 'A dark page with gold and violet.', colors: { background: '#16161F', text: '#F5F3EE', main: '#C9A227', second: '#8E7CC3', accent: '#E07A5F' } },
  { name: 'Monochrome', description: 'Black, white and greys.', colors: { background: '#F5F5F5', text: '#000000', main: '#111111', second: '#555555', accent: '#999999' } },
]

const HEX = /^#[0-9A-Fa-f]{6}$/

export function isHex(value: string): boolean {
  return HEX.test(value)
}

/** A stored palette entry as a hex: flat ('#68315A') or the legacy { hex } shape. */
function hexOf(value: unknown): string | null {
  const h =
    typeof value === 'string'
      ? value
      : value && typeof value === 'object' && typeof (value as { hex?: unknown }).hex === 'string'
        ? (value as { hex: string }).hex
        : null
  return h && HEX.test(h.trim()) ? h.trim().toUpperCase() : null
}

/** Link-in-bio palette → roles, filling gaps from `fallback`. */
export function rolesFromBioPalette(palette: ColorPalette | undefined, fallback: PaletteRoles): PaletteRoles {
  const pick = (key: keyof ColorPalette, role: RoleKey) => hexOf(palette?.[key]) || fallback[role]
  return {
    background: pick('base-100', 'background'),
    text: pick('neutral', 'text'),
    main: pick('primary', 'main'),
    second: pick('secondary', 'second'),
    accent: pick('accent', 'accent'),
  }
}

/** Roles → the link-in-bio palette the API stores. */
export function bioPaletteFromRoles(c: PaletteRoles): ColorPalette {
  return { 'base-100': c.background, neutral: c.text, primary: c.main, secondary: c.second, accent: c.accent }
}

export function samePalette(a: PaletteRoles | null | undefined, b: PaletteRoles | null | undefined): boolean {
  if (!a || !b) return a === b
  return ROLE_FIELDS.every(({ key }) => a[key].toUpperCase() === b[key].toUpperCase())
}

// WCAG contrast, so the editor can warn before an artist saves unreadable text.
function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}
